import { PublicationStatus } from '@prisma/client';

import cdnImageUrl from '../cdn/cdnImageUrl';
import { BucketName } from '../s3';

interface GetPublicationImageUrlParams {
  publicationId: string;
  imageName: string | null | undefined;
  status?: PublicationStatus;
  bucketName?: BucketName;
}

function extractImageName(raw: string): string {
  const value = raw.trim();
  if (!value) return value;

  // Can be a full URL or just a file name
  try {
    const url = new URL(value);
    const parts = url.pathname.split('/');
    return parts[parts.length - 1] || value;
  } catch {
    const parts = value.split('/');
    return parts[parts.length - 1] || value;
  }
}

/**
 * Builds a public image URL for a publication, taking into account its upload status.
 *
 * Behavior:
 * - When the publication status is `PENDING`, returns an internal API URL
 *   (`/api/publication/image?...`) that:
 *   - first tries to read the image buffer from Redis, and
 *   - if it's no longer in Redis, redirects to the CDN/S3 URL.
 * - For all other statuses (`READY`, `PARTIAL`, `FAILED`), returns a direct CDN/S3 URL
 *   using `cdnImageUrl` and the provided `bucketName` / `publicationId`.
 *
 * This helper centralizes the logic of:
 * - handling full URLs vs plain file names (via `extractImageName`),
 * - choosing between Redis/API and CDN based on `PublicationStatus`,
 * - consistently constructing paths like `${bucket}/${publicationId}/${imageName}`.
 *
 * @param publicationId - Unique identifier of the publication (used as subPath in storage).
 * @param imageName - Raw image value from the DB (can be a file name or a full URL).
 * @param status - Current `PublicationStatus` of the publication.
 * @param bucketName - S3 bucket name where images are stored; defaults to `pets-sale-bucket`.
 *
 * @returns A fully qualified URL string or `null` if the input is empty.
 */
export function getPublicationImageUrl({
  publicationId,
  imageName,
  status,
  bucketName = 'pets-sale-bucket'
}: GetPublicationImageUrlParams): string | null {
  const raw = (imageName ?? '').trim();
  if (!raw) return null;

  const normalizedName = extractImageName(raw);

  if (!normalizedName) return null;

  if (status === PublicationStatus.PENDING) {
    // While publication is in upload queue — fetch image via API (Redis → CDN)
    return `/api/publication/image?publicationId=${publicationId}&imageName=${encodeURIComponent(
      normalizedName
    )}`;
  }

  // READY / PARTIAL / FAILED — direct bucket path, CDN will resolve the domain
  return cdnImageUrl({
    bucketName,
    imageName: normalizedName,
    subPath: publicationId
  });
}
